import React from "react";
import { Link } from "@natera/navigation";
import { useNavigate } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import Svg from "@natera/material/lib/svg";
import classNames from "classnames";
import ArrowIcon from "@assets/svg/icons/arrow.svg";
import CloseIcon from "@assets/svg/icons/close.svg";
import "./blueHeader.scss";
import { IconButton } from "@natera/material/lib/button";

export const messages = defineMessages({
  blueHeaderBack: {
    id: "blueHeaderBack",
    defaultMessage: "Back",
  },
  blueHeaderClose: {
    id: "blueHeaderClose",
    defaultMessage: "Close",
  },
});

interface BackArrowProps {
  title: string;
  arrowRoute?: string;
  /** Forwarded to react-router `Link` so back navigation keeps `location.state` (e.g. test history). */
  arrowState?: unknown;
  /** Back-arrow as a callback (e.g. step-aware navigation). Used when `arrowRoute` is not provided. */
  arrowButton?: () => void;
  crossRoute?: string;
  crossButton?: () => void;
  /** Constrains the header content to 600px so it lines up with a medium-width body. */
  medium?: boolean;
  /** Keeps the header's footprint (height/margin) but renders it visually hidden. */
  invisible?: boolean;
}

const BlueHeader: React.FC<BackArrowProps> = ({
  title,
  arrowRoute,
  arrowState,
  arrowButton,
  crossRoute,
  crossButton,
  medium = false,
  invisible = false,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();

  const handleClose = crossButton
    ? crossButton
    : crossRoute
      ? () => navigate(crossRoute)
      : undefined;

  return (
    <div
      className={classNames("blue-header", {
        "blue-header--medium": medium,
        "blue-header--invisible": invisible,
      })}
      aria-hidden={invisible || undefined}
    >
      <div className="blue-header__inner">
        <div className="blue-header__left">
          {arrowRoute ? (
            <Link
              to={arrowRoute}
              state={arrowState}
              role="button"
              aria-label={intl.formatMessage(messages.blueHeaderBack)}
              className="blue-header__icon-button"
            >
              <Svg content={ArrowIcon} />
            </Link>
          ) : (
            arrowButton && (
              <IconButton
                onClick={arrowButton}
                aria-label={intl.formatMessage(messages.blueHeaderBack)}
                className="blue-header__icon-button"
              >
                {ArrowIcon}
              </IconButton>
            )
          )}
        </div>

        <h1 className="blue-header__title">{title}</h1>

        <div className="blue-header__right">
          {handleClose && (
            <IconButton
              onClick={handleClose}
              aria-label={intl.formatMessage(messages.blueHeaderClose)}
              className="blue-header__icon-button"
            >
              {CloseIcon}
            </IconButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlueHeader;
